import React, { useState, useEffect, useCallback, useRef } from 'react';
import DashboardLayout from '../layout/DashboardLayout';
import { config } from '../../config/config';
import { useSelector } from 'react-redux';
import { useThemeColors } from '../../hooks/useThemeColors';
import LeadDetailsDialog from '../leads/LeadDetailsDialog';
import ProjectManagerSearch from '../leads/ProjectManagerSearch';
import SearchableDropdown from '../common/SearchableDropdown';
import { PRIORITIZED_CITIES } from '../../data/cityData';
import { showToast } from '../utils/Toast';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    LineElement,
    PointElement,
    Filler,
} from 'chart.js';
import {
    EnvelopeIcon,
    PhoneIcon,
    BuildingOfficeIcon,
    MapPinIcon,
    CalendarIcon,
    ChevronDownIcon,
    MagnifyingGlassIcon,
    ClipboardDocumentIcon,
    CheckIcon,
    WrenchScrewdriverIcon,
    UserIcon,
    CheckCircleIcon,
    ClockIcon,
    ChatBubbleLeftIcon,
    PaperAirplaneIcon,
    ArrowRightCircleIcon,
    XMarkIcon,
    FunnelIcon,
    InboxIcon,
    DocumentTextIcon,
    PhoneArrowUpRightIcon,
    NoSymbolIcon,
    SparklesIcon,
    ChartBarIcon,
    EyeIcon,
    EyeSlashIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';

ChartJS.register(
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    LineElement,
    PointElement,
    Filler
);

const API_URL = config.apiUrl;

const STATUS_CONFIG = {
    new: { label: 'New', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500', icon: SparklesIcon },
    acknowledged: { label: 'Acknowledged', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500', icon: CheckCircleIcon },
    contacted: { label: 'Contacted', bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200', dot: 'bg-violet-500', icon: PhoneArrowUpRightIcon },
    converted: { label: 'Converted', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500', icon: ArrowRightCircleIcon },
    closed: { label: 'Closed', bg: 'bg-gray-50', text: 'text-gray-500', border: 'border-gray-200', dot: 'bg-gray-400', icon: NoSymbolIcon }
};

const ALL_STATUSES = ['new', 'acknowledged', 'contacted', 'converted', 'closed'];

/* ──────────────────── Convert To Lead Dialog ──────────────────── */
const ConvertToLeadDialog = ({ isOpen, onClose, inquiry, inquiryType, onConvert }) => {
    const [selectedManagers, setSelectedManagers] = useState([]);
    const [city, setCity] = useState('');
    const [street, setStreet] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [clientAddress, setClientAddress] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen && inquiry) {
            setStreet(inquiry.propertyAddress || '');
            setCity('');
            setSelectedManagers([]);
            // Prefill phone, email, address from inquiry (client data)
            setPhone(inquiry.phone || '');
            setEmail(inquiry.email || '');
            setClientAddress(inquiry.propertyAddress || '');
        }
    }, [isOpen, inquiry]);

    if (!isOpen || !inquiry) return null;

    const fullName = inquiryType === 'quotes'
        ? (inquiry.fullName || `${inquiry.firstName || ''} ${inquiry.lastName || ''}`.trim())
        : inquiry.name;

    const handleConvert = async () => {
        if (selectedManagers.length === 0) {
            showToast('Please select at least one project manager', 'error');
            return;
        }
        if (!city) {
            showToast('Please select a city', 'error');
            return;
        }
        setLoading(true);
        try {
            await onConvert({
                projectManagers: selectedManagers.map(m => m._id),
                address: { street, city },
                clientPhone: phone,
                clientEmail: email,
                clientAddress
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
                <div className="px-6 pt-6 pb-4">
                    <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                                <ArrowRightCircleIcon className="w-5 h-5 text-emerald-600" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">Convert to Lead</h3>
                                <p className="text-sm text-gray-500">Create a new lead from this inquiry</p>
                            </div>
                        </div>
                        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                            <XMarkIcon className="w-5 h-5 text-gray-400" />
                        </button>
                    </div>
                </div>

                <div className="px-6 pb-4 space-y-4 max-h-[60vh] overflow-y-auto">
                    <div className="rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 p-4 border border-blue-100">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-white shadow-sm flex items-center justify-center">
                                <UserIcon className="w-4 h-4 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-gray-900">{fullName}</p>
                                <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                                    {inquiry.email && <span>{inquiry.email}</span>}
                                    {inquiry.phone && <span>{inquiry.phone}</span>}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Phone
                        </label>
                        <div className="relative">
                            <PhoneIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="tel"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow"
                                placeholder="Client phone..."
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Email
                        </label>
                        <div className="relative">
                            <EnvelopeIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow"
                                placeholder="Client email..."
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Client Address
                        </label>
                        <div className="relative">
                            <MapPinIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                value={clientAddress}
                                onChange={(e) => setClientAddress(e.target.value)}
                                className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow"
                                placeholder="Client address..."
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Project Manager(s) <span className="text-red-500">*</span>
                        </label>
                        <ProjectManagerSearch
                            selectedManagers={selectedManagers}
                            onManagerSelect={(manager) => setSelectedManagers(prev => [...prev, manager])}
                            onManagerRemove={(managerId) => setSelectedManagers(prev => prev.filter(m => m._id !== managerId))}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            City <span className="text-red-500">*</span>
                        </label>
                        <SearchableDropdown
                            options={PRIORITIZED_CITIES}
                            value={city}
                            onChange={(val) => setCity(val)}
                            placeholder="Select city..."
                            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Street Address</label>
                        <input
                            type="text"
                            value={street}
                            onChange={(e) => setStreet(e.target.value)}
                            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow"
                            placeholder="Enter street address..."
                        />
                    </div>
                </div>

                <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50/80 border-t border-gray-100">
                    <button
                        onClick={onClose}
                        className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConvert}
                        disabled={loading || selectedManagers.length === 0 || !city}
                        className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                    >
                        {loading ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white" />
                        ) : (
                            <ArrowRightCircleIcon className="w-4 h-4" />
                        )}
                        Create Lead
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ──────────────────── Status Badge ──────────────────── */
const StatusBadge = ({ status }) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.new;
    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${cfg.bg} ${cfg.text} border ${cfg.border}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
            {cfg.label}
        </span>
    );
};

/* ──────────────────── Main Component ──────────────────── */
const WebsiteInquiries = () => {
    const { token } = useSelector((state) => state.auth);
    const { buttonColors } = useThemeColors();

    const [activeTab, setActiveTab] = useState('quotes');
    const [statusFilter, setStatusFilter] = useState('all');
    const [quotes, setQuotes] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedRow, setExpandedRow] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [copiedField, setCopiedField] = useState(null);
    const [actionLoading, setActionLoading] = useState(null);
    const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [showFilters, setShowFilters] = useState(false);

    const [noteText, setNoteText] = useState('');
    const [noteLoading, setNoteLoading] = useState(false);

    const [convertDialogOpen, setConvertDialogOpen] = useState(false);
    const [convertInquiry, setConvertInquiry] = useState(null);

    const [selectedLead, setSelectedLead] = useState(null);
    const [isLeadDetailsOpen, setIsLeadDetailsOpen] = useState(false);

    const [showInsights, setShowInsights] = useState(false);
    const [analytics, setAnalytics] = useState(null);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);

    const searchTimeoutRef = useRef(null);

    /* ── fetch analytics ── */
    useEffect(() => {
        if (!showInsights || analytics) return;
        const fetchAnalytics = async () => {
            setAnalyticsLoading(true);
            try {
                const response = await fetch(`${API_URL}/api/inquiries/analytics`, {
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
                });
                const data = await response.json();
                if (data.success) setAnalytics(data.data);
            } catch (err) {
                console.error('Error fetching analytics:', err);
            } finally {
                setAnalyticsLoading(false);
            }
        };
        fetchAnalytics();
    }, [showInsights, analytics, token]);

    /* ── helpers ── */
    const copyToClipboard = (text, fieldId) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopiedField(fieldId);
            setTimeout(() => setCopiedField(null), 2000);
        });
    };

    const CopyButton = ({ text, fieldId }) => (
        <button
            onClick={(e) => { e.stopPropagation(); copyToClipboard(text, fieldId); }}
            className="ml-1.5 p-1 rounded-md hover:bg-gray-100 transition-colors"
            title="Copy"
        >
            {copiedField === fieldId ? (
                <CheckIcon className="w-3.5 h-3.5 text-emerald-500" />
            ) : (
                <ClipboardDocumentIcon className="w-3.5 h-3.5 text-gray-300 hover:text-gray-500" />
            )}
        </button>
    );

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    const formatShortDate = (dateString) => {
        if (!dateString) return '-';
        const d = new Date(dateString);
        const now = new Date();
        const diffMs = now - d;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays}d ago`;
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const hasActiveFilters = startDate || endDate || statusFilter !== 'all';

    /* ── data fetching ── */
    const fetchData = useCallback(async (type) => {
        try {
            const params = new URLSearchParams({
                page: pagination.page.toString(),
                limit: pagination.limit.toString(),
                search: searchTerm,
                ...(statusFilter !== 'all' && { status: statusFilter }),
                ...(startDate && { startDate }),
                ...(endDate && { endDate })
            });

            const response = await fetch(
                `${API_URL}/api/inquiries/${type}?${params.toString()}`,
                { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
            );
            const data = await response.json();
            if (data.success) {
                if (type === 'quotes') setQuotes(data.data);
                else setContacts(data.data);
                setPagination(prev => ({ ...prev, ...data.pagination }));
            }
        } catch (error) {
            console.error(`Error fetching ${type}:`, error);
        }
    }, [token, pagination.page, pagination.limit, searchTerm, statusFilter, startDate, endDate]);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await fetchData(activeTab);
            setLoading(false);
        };
        loadData();
    }, [activeTab, fetchData]);

    const refreshData = () => fetchData(activeTab);

    /* ── handlers ── */
    const toggleRow = (id) => { setExpandedRow(expandedRow === id ? null : id); setNoteText(''); };
    const handleTabChange = (tab) => { setActiveTab(tab); setExpandedRow(null); setPagination(prev => ({ ...prev, page: 1 })); };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => setPagination(prev => ({ ...prev, page: 1 })), 300);
    };

    const handleStatusChange = async (type, id, newStatus) => {
        setActionLoading(id);
        try {
            const response = await fetch(`${API_URL}/api/inquiries/${type}/${id}/status`, {
                method: 'PATCH',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: newStatus })
            });
            const data = await response.json();
            if (data.success) { showToast(`Status updated to ${STATUS_CONFIG[newStatus]?.label}`, 'success'); await refreshData(); }
            else showToast(data.message || 'Failed to update status', 'error');
        } catch { showToast('Failed to update status', 'error'); }
        finally { setActionLoading(null); }
    };

    const handleAddNote = async (type, id) => {
        if (!noteText.trim()) return;
        setNoteLoading(true);
        try {
            const response = await fetch(`${API_URL}/api/inquiries/${type}/${id}/notes`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ content: noteText })
            });
            const data = await response.json();
            if (data.success) { showToast('Note added', 'success'); setNoteText(''); await refreshData(); }
            else showToast(data.message || 'Failed to add note', 'error');
        } catch { showToast('Failed to add note', 'error'); }
        finally { setNoteLoading(false); }
    };

    const handleConvertConfirm = async (convertData) => {
        try {
            const response = await fetch(`${API_URL}/api/inquiries/${activeTab}/${convertInquiry._id}/convert`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(convertData)
            });
            const data = await response.json();
            if (data.success) {
                showToast('Successfully converted to lead!', 'success');
                setConvertDialogOpen(false);
                setConvertInquiry(null);
                await refreshData();
                setSelectedLead(data.lead);
                setIsLeadDetailsOpen(true);
            } else {
                showToast(data.message || 'Failed to convert', 'error');
            }
        } catch { showToast('Failed to convert to lead', 'error'); }
    };

    const clearFilters = () => { setStartDate(''); setEndDate(''); setStatusFilter('all'); setPagination(prev => ({ ...prev, page: 1 })); };

    /* ── expanded detail panel ── */
    const renderExpandedContent = (item, type) => {
        const isQuote = type === 'quotes';
        const fullName = isQuote
            ? (item.fullName || `${item.firstName || ''} ${item.lastName || ''}`.trim())
            : item.name;
        const currentStatus = item.status || (item.acknowledged ? 'acknowledged' : 'new');
        const isConverted = currentStatus === 'converted' || !!item.convertedToLead;

        const activities = item.activityLog || [];
        const notes = item.notes || [];
        const timeline = [
            ...activities.map(a => ({ ...a, _type: 'activity', sortDate: new Date(a.timestamp) })),
            ...notes.map(n => ({ ...n, _type: 'note', sortDate: new Date(n.createdAt) }))
        ].sort((a, b) => b.sortDate - a.sortDate);

        const InfoRow = ({ icon: Icon, label, children, copyText, copyId }) => (
            <div className="flex items-start py-2 group/row">
                <div className="w-28 flex-shrink-0 flex items-center gap-2 text-xs text-gray-400 pt-0.5">
                    <Icon className="w-3.5 h-3.5" />
                    <span>{label}</span>
                </div>
                <div className="flex-1 min-w-0 flex items-center gap-1.5 text-sm text-gray-800">
                    {children}
                    {copyText && (
                        <button
                            onClick={(e) => { e.stopPropagation(); copyToClipboard(copyText, copyId); }}
                            className="opacity-0 group-hover/row:opacity-100 p-0.5 rounded hover:bg-gray-100 transition-all"
                        >
                            {copiedField === copyId
                                ? <CheckIcon className="w-3 h-3 text-emerald-500" />
                                : <ClipboardDocumentIcon className="w-3 h-3 text-gray-300" />
                            }
                        </button>
                    )}
                </div>
            </div>
        );

        return (
            <tr>
                <td colSpan={6} className="p-0">
                    <div className="border-t border-gray-100 bg-gray-50/50">

                        {/* ── TOP BAR: Status pills + Convert button ── */}
                        <div className="px-6 py-3 bg-white border-b border-gray-100 flex flex-wrap items-center gap-x-6 gap-y-2">
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-medium text-gray-400 mr-1">Status:</span>
                                {ALL_STATUSES.filter(s => s !== 'converted').map(s => {
                                    const cfg = STATUS_CONFIG[s];
                                    const Icon = cfg.icon;
                                    const isActive = currentStatus === s;
                                    return (
                                        <button
                                            key={s}
                                            onClick={(e) => { e.stopPropagation(); if (!isActive) handleStatusChange(type, item._id, s); }}
                                            disabled={isActive || isConverted || actionLoading === item._id}
                                            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                                                isActive
                                                    ? `${cfg.bg} ${cfg.text} ${cfg.border} shadow-sm`
                                                    : isConverted
                                                        ? 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed'
                                                        : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300 hover:text-gray-700'
                                            }`}
                                        >
                                            <Icon className="w-3.5 h-3.5" />
                                            {cfg.label}
                                        </button>
                                    );
                                })}
                                {actionLoading === item._id && (
                                    <div className="animate-spin rounded-full h-3.5 w-3.5 border-[1.5px] border-gray-300 border-t-gray-600" />
                                )}
                            </div>
                            <div className="ml-auto">
                                {!isConverted ? (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setConvertInquiry(item); setConvertDialogOpen(true); }}
                                        className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors shadow-sm"
                                    >
                                        <ArrowRightCircleIcon className="w-3.5 h-3.5" />
                                        Convert to Lead
                                    </button>
                                ) : (
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 rounded-lg border border-emerald-200">
                                        <CheckCircleSolidIcon className="w-3.5 h-3.5 text-emerald-500" />
                                        Converted {item.convertedAt && `· ${formatShortDate(item.convertedAt)}`}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* ── BODY: Two columns ── */}
                        <div className="flex flex-col lg:flex-row divide-y lg:divide-y-0 lg:divide-x divide-gray-100">

                            {/* LEFT: Contact info + Message */}
                            <div className="flex-1 min-w-0 p-5">
                                <div className="divide-y divide-gray-50">
                                    <InfoRow icon={UserIcon} label="Name" copyText={fullName} copyId={`${item._id}-name`}>
                                        <span className="font-medium">{fullName}</span>
                                    </InfoRow>
                                    {isQuote && item.companyName && (
                                        <InfoRow icon={BuildingOfficeIcon} label="Company" copyText={item.companyName} copyId={`${item._id}-company`}>
                                            <span>{item.companyName}</span>
                                        </InfoRow>
                                    )}
                                    {item.email && (
                                        <InfoRow icon={EnvelopeIcon} label="Email" copyText={item.email} copyId={`${item._id}-email`}>
                                            <a href={`mailto:${item.email}`} className="text-blue-600 hover:underline truncate">{item.email}</a>
                                        </InfoRow>
                                    )}
                                    {item.phone && (
                                        <InfoRow icon={PhoneIcon} label="Phone" copyText={item.phone} copyId={`${item._id}-phone`}>
                                            <a href={`tel:${item.phone}`} className="text-blue-600 hover:underline">{item.phone}</a>
                                        </InfoRow>
                                    )}
                                    {isQuote && item.propertyAddress && (
                                        <InfoRow icon={MapPinIcon} label="Address" copyText={item.propertyAddress} copyId={`${item._id}-address`}>
                                            <span className="truncate">{item.propertyAddress}</span>
                                        </InfoRow>
                                    )}
                                    {isQuote && item.service && (
                                        <InfoRow icon={WrenchScrewdriverIcon} label="Service">
                                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">{item.service}</span>
                                        </InfoRow>
                                    )}
                                    {!isQuote && item.subject && (
                                        <InfoRow icon={DocumentTextIcon} label="Subject">
                                            <span>{item.subject}</span>
                                        </InfoRow>
                                    )}
                                    <InfoRow icon={CalendarIcon} label="Submitted">
                                        <span className="text-gray-500 text-xs">{formatDate(item.createdAt)}{item.source ? ` · via ${item.source}` : ''}</span>
                                    </InfoRow>
                                </div>

                                {item.message && (
                                    <div className="mt-4 pt-4 border-t border-gray-100">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Message</span>
                                            <CopyButton text={item.message} fieldId={`${item._id}-message`} />
                                        </div>
                                        <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed bg-white rounded-lg p-3 border border-gray-100">{item.message}</p>
                                    </div>
                                )}
                            </div>

                            {/* RIGHT: Notes + Activity */}
                            <div className="lg:w-[340px] flex-shrink-0 flex flex-col">
                                {/* Note input */}
                                <div className="p-4 border-b border-gray-100">
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            value={noteText}
                                            onChange={(e) => setNoteText(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === 'Enter' && noteText.trim()) { e.stopPropagation(); handleAddNote(type, item._id); } }}
                                            onClick={(e) => e.stopPropagation()}
                                            placeholder="Add a note..."
                                            className="flex-1 px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow"
                                        />
                                        <button
                                            onClick={(e) => { e.stopPropagation(); handleAddNote(type, item._id); }}
                                            disabled={!noteText.trim() || noteLoading}
                                            className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                                        >
                                            {noteLoading
                                                ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white" />
                                                : <PaperAirplaneIcon className="w-4 h-4" />
                                            }
                                        </button>
                                    </div>
                                </div>

                                {/* Activity timeline */}
                                <div className="flex-1 overflow-y-auto max-h-64">
                                    {timeline.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center py-10 text-gray-300">
                                            <ClockIcon className="w-6 h-6 mb-1" />
                                            <p className="text-xs text-gray-400">No activity yet</p>
                                        </div>
                                    ) : (
                                        <div className="p-4 space-y-0">
                                            {timeline.map((entry, idx) => {
                                                const isNote = entry._type === 'note';
                                                const isConversion = entry.action === 'converted_to_lead';
                                                return (
                                                    <div key={idx} className="flex gap-3 relative pb-4 last:pb-0">
                                                        {idx < timeline.length - 1 && (
                                                            <div className="absolute left-[7px] top-5 bottom-0 w-px bg-gray-100" />
                                                        )}
                                                        <div className="flex-shrink-0 mt-1 z-10">
                                                            <div className={`w-3.5 h-3.5 rounded-full border-2 border-gray-50/80 ${
                                                                isNote ? 'bg-blue-400' : isConversion ? 'bg-emerald-400' : 'bg-gray-300'
                                                            }`} />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            {isNote ? (
                                                                <p className="text-xs text-gray-700 leading-relaxed">{entry.content}</p>
                                                            ) : isConversion ? (
                                                                <p className="text-xs text-emerald-700 font-medium">Converted to Lead{entry.jobNumber ? ` ${entry.jobNumber}` : ''}</p>
                                                            ) : (
                                                                <p className="text-xs text-gray-600">
                                                                    Status → <span className="font-medium">{STATUS_CONFIG[entry.status]?.label || entry.status}</span>
                                                                </p>
                                                            )}
                                                            <p className="text-[10px] text-gray-400 mt-0.5">
                                                                {entry.performedByName || entry.createdByName || 'System'} · {formatShortDate(entry.timestamp || entry.createdAt)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </td>
            </tr>
        );
    };

    /* ── table row ── */
    const renderRow = (item, type) => {
        const isExpanded = expandedRow === item._id;
        const isQuote = type === 'quotes';
        const fullName = isQuote
            ? (item.fullName || `${item.firstName || ''} ${item.lastName || ''}`.trim())
            : item.name;
        const currentStatus = item.status || (item.acknowledged ? 'acknowledged' : 'new');
        const isConverted = currentStatus === 'converted' || !!item.convertedToLead;

        return (
            <React.Fragment key={item._id}>
                <tr
                    className={`group border-b border-gray-50 cursor-pointer transition-all duration-150 ${
                        isExpanded ? 'bg-slate-50' : 'hover:bg-gray-50/60'
                    } ${isConverted ? 'opacity-60' : ''}`}
                    onClick={() => toggleRow(item._id)}
                >
                    <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2.5">
                            <div className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
                                <ChevronDownIcon className="w-4 h-4 text-gray-300 group-hover:text-gray-500" />
                            </div>
                            <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center flex-shrink-0">
                                    <span className="text-xs font-semibold text-blue-700">
                                        {fullName?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() || '?'}
                                    </span>
                                </div>
                                <div>
                                    <span className="font-medium text-gray-900 text-sm">{fullName}</span>
                                    {isQuote && item.companyName && (
                                        <p className="text-xs text-gray-400">{item.companyName}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-gray-500 max-w-[200px] truncate">
                        {item.email || '-'}
                    </td>
                    <td className="px-4 py-3.5">
                        {isQuote && item.service ? (
                            <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                                {item.service}
                            </span>
                        ) : !isQuote && item.subject ? (
                            <span className="text-sm text-gray-500 truncate block max-w-[160px]">{item.subject}</span>
                        ) : (
                            <span className="text-gray-300 text-sm">—</span>
                        )}
                    </td>
                    <td className="px-4 py-3.5 text-sm text-gray-400">{formatShortDate(item.createdAt)}</td>
                    <td className="px-4 py-3.5">
                        <StatusBadge status={currentStatus} />
                    </td>
                    <td className="px-4 py-3.5">
                        <div className="flex items-center gap-2">
                            {(item.notes?.length > 0) && (
                                <span className="inline-flex items-center gap-1 text-[11px] text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded" title={`${item.notes.length} note(s)`}>
                                    <ChatBubbleLeftIcon className="w-3 h-3" />
                                    {item.notes.length}
                                </span>
                            )}
                            {isConverted && (
                                <CheckCircleSolidIcon className="w-4 h-4 text-emerald-500" title="Converted to Lead" />
                            )}
                        </div>
                    </td>
                </tr>
                {isExpanded && renderExpandedContent(item, type)}
            </React.Fragment>
        );
    };

    const currentData = activeTab === 'quotes' ? quotes : contacts;

    return (
        <DashboardLayout>
            <div className="p-4 sm:p-6 max-w-[1600px] mx-auto">
                {/* ── Header ── */}
                <div className="mb-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div>
                            <h1 className="text-xl font-bold text-gray-900">Website Inquiries</h1>
                            <p className="text-sm text-gray-500 mt-0.5">Manage and convert website submissions into leads</p>
                        </div>
                        <div className="flex items-center gap-2.5">
                            {/* Search */}
                            <div className="relative">
                                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search inquiries..."
                                    value={searchTerm}
                                    onChange={handleSearch}
                                    className="w-56 pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition-shadow"
                                />
                            </div>
                            {/* Insights toggle */}
                            <button
                                onClick={() => setShowInsights(!showInsights)}
                                className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium border rounded-xl transition-all ${
                                    showInsights
                                        ? 'border-indigo-200 bg-indigo-50 text-indigo-700 shadow-sm'
                                        : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                                }`}
                            >
                                <ChartBarIcon className="w-4 h-4" />
                                Insights
                            </button>
                            {/* Filter */}
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium border rounded-xl transition-all ${
                                    hasActiveFilters
                                        ? 'border-blue-200 bg-blue-50 text-blue-700 shadow-sm'
                                        : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                                }`}
                            >
                                <FunnelIcon className="w-4 h-4" />
                                Filters
                                {hasActiveFilters && <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
                            </button>
                        </div>
                    </div>

                    {/* Filters panel */}
                    {showFilters && (
                        <div className="mt-3 bg-white rounded-xl border border-gray-200 shadow-sm p-4 animate-in slide-in-from-top-2">
                            <div className="flex flex-wrap items-end gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
                                    <input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPagination(prev => ({ ...prev, page: 1 })); }}
                                        className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500" />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
                                    <input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPagination(prev => ({ ...prev, page: 1 })); }}
                                        className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500" />
                                </div>
                                {hasActiveFilters && (
                                    <button onClick={clearFilters} className="px-3 py-2 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors">
                                        Reset filters
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Insights Panel ── */}
                {showInsights && (
                    <div className="mb-6 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                        {analyticsLoading || !analytics ? (
                            <div className="flex items-center justify-center py-16">
                                <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-300 border-t-indigo-600" />
                            </div>
                        ) : (
                            <>
                                {/* Stat cards row */}
                                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 p-5 border-b border-gray-100">
                                    <div className="rounded-xl bg-gradient-to-br from-indigo-50 to-blue-50 p-4 border border-indigo-100">
                                        <p className="text-xs font-medium text-indigo-600 uppercase tracking-wider">Total Inquiries</p>
                                        <p className="text-2xl font-bold text-gray-900 mt-1">{(analytics.totalQuotes + analytics.totalContacts).toLocaleString()}</p>
                                    </div>
                                    <div className="rounded-xl bg-gradient-to-br from-blue-50 to-cyan-50 p-4 border border-blue-100">
                                        <p className="text-xs font-medium text-blue-600 uppercase tracking-wider">Quote Requests</p>
                                        <p className="text-2xl font-bold text-gray-900 mt-1">{analytics.totalQuotes.toLocaleString()}</p>
                                    </div>
                                    <div className="rounded-xl bg-gradient-to-br from-violet-50 to-purple-50 p-4 border border-violet-100">
                                        <p className="text-xs font-medium text-violet-600 uppercase tracking-wider">Contact Messages</p>
                                        <p className="text-2xl font-bold text-gray-900 mt-1">{analytics.totalContacts.toLocaleString()}</p>
                                    </div>
                                    <div className="rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 p-4 border border-emerald-100">
                                        <p className="text-xs font-medium text-emerald-600 uppercase tracking-wider">Conversion Rate</p>
                                        <p className="text-2xl font-bold text-gray-900 mt-1">
                                            {((analytics.statusBreakdown.converted || 0) / Math.max(1, analytics.totalQuotes + analytics.totalContacts) * 100).toFixed(1)}%
                                        </p>
                                    </div>
                                </div>

                                {/* Charts row */}
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 lg:divide-x divide-gray-100">
                                    {/* Monthly trend */}
                                    <div className="lg:col-span-2 p-5">
                                        <p className="text-sm font-semibold text-gray-900 mb-3">Monthly Trend <span className="text-xs font-normal text-gray-400">(Last 6 months)</span></p>
                                        <div className="h-52">
                                            <Line
                                                data={{
                                                    labels: analytics.monthlyLabels,
                                                    datasets: [
                                                        {
                                                            label: 'Quotes',
                                                            data: analytics.quotesByMonth,
                                                            borderColor: '#6366F1',
                                                            backgroundColor: 'rgba(99, 102, 241, 0.08)',
                                                            fill: true,
                                                            tension: 0.35,
                                                            pointRadius: 4,
                                                            pointBackgroundColor: '#6366F1',
                                                            borderWidth: 2
                                                        },
                                                        {
                                                            label: 'Contacts',
                                                            data: analytics.contactsByMonth,
                                                            borderColor: '#8B5CF6',
                                                            backgroundColor: 'rgba(139, 92, 246, 0.06)',
                                                            fill: true,
                                                            tension: 0.35,
                                                            pointRadius: 4,
                                                            pointBackgroundColor: '#8B5CF6',
                                                            borderWidth: 2
                                                        }
                                                    ]
                                                }}
                                                options={{
                                                    responsive: true,
                                                    maintainAspectRatio: false,
                                                    plugins: {
                                                        legend: { position: 'top', labels: { boxWidth: 12, usePointStyle: true, pointStyle: 'circle', font: { size: 11 } } },
                                                        tooltip: { mode: 'index', intersect: false }
                                                    },
                                                    scales: {
                                                        y: { beginAtZero: true, ticks: { stepSize: 1, font: { size: 11 } }, grid: { color: 'rgba(0,0,0,0.04)' } },
                                                        x: { ticks: { font: { size: 11 } }, grid: { display: false } }
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {/* Status doughnut */}
                                    <div className="p-5">
                                        <p className="text-sm font-semibold text-gray-900 mb-3">Status Distribution</p>
                                        <div className="h-40 flex items-center justify-center">
                                            <Doughnut
                                                data={{
                                                    labels: Object.keys(analytics.statusBreakdown).map(s => STATUS_CONFIG[s]?.label || s),
                                                    datasets: [{
                                                        data: Object.values(analytics.statusBreakdown),
                                                        backgroundColor: Object.keys(analytics.statusBreakdown).map(s => {
                                                            const colors = { new: '#3B82F6', acknowledged: '#F59E0B', contacted: '#8B5CF6', converted: '#10B981', closed: '#9CA3AF' };
                                                            return colors[s] || '#D1D5DB';
                                                        }),
                                                        borderWidth: 0,
                                                        hoverOffset: 4
                                                    }]
                                                }}
                                                options={{
                                                    responsive: true,
                                                    maintainAspectRatio: false,
                                                    cutout: '68%',
                                                    plugins: {
                                                        legend: { position: 'bottom', labels: { boxWidth: 10, usePointStyle: true, pointStyle: 'circle', padding: 12, font: { size: 10 } } },
                                                        tooltip: { enabled: true }
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Service breakdown bar (if we have data) */}
                                {analytics.serviceBreakdown.length > 0 && (
                                    <div className="p-5 border-t border-gray-100">
                                        <p className="text-sm font-semibold text-gray-900 mb-3">Top Requested Services</p>
                                        <div className="h-44">
                                            <Bar
                                                data={{
                                                    labels: analytics.serviceBreakdown.map(s => s.service),
                                                    datasets: [{
                                                        label: 'Requests',
                                                        data: analytics.serviceBreakdown.map(s => s.count),
                                                        backgroundColor: [
                                                            'rgba(99, 102, 241, 0.75)', 'rgba(139, 92, 246, 0.75)',
                                                            'rgba(59, 130, 246, 0.75)', 'rgba(16, 185, 129, 0.75)',
                                                            'rgba(245, 158, 11, 0.75)', 'rgba(239, 68, 68, 0.65)',
                                                            'rgba(156, 163, 175, 0.65)', 'rgba(107, 114, 128, 0.55)'
                                                        ],
                                                        borderRadius: 6,
                                                        borderSkipped: false,
                                                        barThickness: 28
                                                    }]
                                                }}
                                                options={{
                                                    responsive: true,
                                                    maintainAspectRatio: false,
                                                    indexAxis: 'y',
                                                    plugins: {
                                                        legend: { display: false },
                                                        tooltip: { enabled: true }
                                                    },
                                                    scales: {
                                                        x: { beginAtZero: true, ticks: { stepSize: 1, font: { size: 11 } }, grid: { color: 'rgba(0,0,0,0.04)' } },
                                                        y: { ticks: { font: { size: 11 } }, grid: { display: false } }
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* ── Type Tabs + Status Pills ── */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                    {/* Quote / Contact Tabs */}
                    <div className="inline-flex bg-gray-100/80 rounded-xl p-1 gap-0.5">
                        <button
                            onClick={() => handleTabChange('quotes')}
                            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                                activeTab === 'quotes'
                                    ? 'bg-white text-gray-900 shadow-sm'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <InboxIcon className="w-4 h-4" />
                            Quote Requests
                            {activeTab === 'quotes' && pagination.total > 0 && (
                                <span className="ml-1 px-1.5 py-0.5 text-[10px] font-semibold bg-blue-100 text-blue-700 rounded-md">{pagination.total}</span>
                            )}
                        </button>
                        <button
                            onClick={() => handleTabChange('contacts')}
                            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                                activeTab === 'contacts'
                                    ? 'bg-white text-gray-900 shadow-sm'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <EnvelopeIcon className="w-4 h-4" />
                            Contact Messages
                            {activeTab === 'contacts' && pagination.total > 0 && (
                                <span className="ml-1 px-1.5 py-0.5 text-[10px] font-semibold bg-blue-100 text-blue-700 rounded-md">{pagination.total}</span>
                            )}
                        </button>
                    </div>

                    {/* Status filter pills */}
                    <div className="flex flex-wrap gap-1.5">
                        {[
                            { id: 'all', label: 'All' },
                            { id: 'open', label: 'Open' },
                            { id: 'new', label: 'New' },
                            { id: 'acknowledged', label: 'Acknowledged' },
                            { id: 'contacted', label: 'Contacted' },
                            { id: 'converted', label: 'Converted' },
                            { id: 'closed', label: 'Closed' }
                        ].map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => { setStatusFilter(tab.id); setPagination(prev => ({ ...prev, page: 1 })); }}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                    statusFilter === tab.id
                                        ? 'bg-gray-900 text-white shadow-sm'
                                        : 'bg-white text-gray-500 border border-gray-200 hover:border-gray-300 hover:text-gray-700'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* ── Table ── */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200/80 overflow-hidden">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-20">
                            <div className="animate-spin rounded-full h-8 w-8 border-[3px] border-gray-200 border-t-blue-600" />
                            <p className="text-sm text-gray-400 mt-3">Loading inquiries...</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-gray-100">
                                        <th className="px-5 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Name</th>
                                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Email</th>
                                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                                            {activeTab === 'quotes' ? 'Service' : 'Subject'}
                                        </th>
                                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Date</th>
                                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Status</th>
                                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider w-20"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentData.length > 0 ? (
                                        currentData.map(item => renderRow(item, activeTab))
                                    ) : (
                                        <tr>
                                            <td colSpan={6} className="py-20 text-center">
                                                <div className="flex flex-col items-center text-gray-300">
                                                    <InboxIcon className="w-12 h-12 mb-3" />
                                                    <p className="text-sm font-medium text-gray-400">
                                                        {hasActiveFilters ? 'No results match your filters' : `No ${activeTab === 'quotes' ? 'quote requests' : 'contact messages'} yet`}
                                                    </p>
                                                    {hasActiveFilters && (
                                                        <button onClick={clearFilters} className="mt-2 text-xs text-blue-600 hover:text-blue-700 font-medium">
                                                            Clear filters
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Pagination */}
                    {!loading && pagination.pages > 1 && (
                        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between bg-gray-50/50">
                            <p className="text-xs text-gray-500">
                                <span className="font-medium text-gray-700">{((pagination.page - 1) * pagination.limit) + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)}</span>
                                {' '}of <span className="font-medium text-gray-700">{pagination.total}</span>
                            </p>
                            <div className="flex gap-1.5">
                                <button
                                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                    disabled={pagination.page === 1}
                                    className="px-3 py-1.5 text-xs font-medium border border-gray-200 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-white transition-colors"
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                    disabled={pagination.page >= pagination.pages}
                                    className="px-3 py-1.5 text-xs font-medium border border-gray-200 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-white transition-colors"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <ConvertToLeadDialog
                isOpen={convertDialogOpen}
                onClose={() => { setConvertDialogOpen(false); setConvertInquiry(null); }}
                inquiry={convertInquiry}
                inquiryType={activeTab}
                onConvert={handleConvertConfirm}
            />

            <LeadDetailsDialog
                isOpen={isLeadDetailsOpen}
                onClose={() => { setIsLeadDetailsOpen(false); setSelectedLead(null); }}
                lead={selectedLead}
            />
        </DashboardLayout>
    );
};

export default WebsiteInquiries;
